import { FiInfo, FiMapPin } from "react-icons/fi";
import { Spinner } from "@chakra-ui/react";

import type { KeyPersonnelProject } from "newui/features/Company/KeyPersonnel/types/KeyPersonnel.type";

interface MobileKeyPersonnelProjectCardProps {
	isLoading?: boolean;
	project: KeyPersonnelProject;
	onClick?: () => void;
}

/**
 * Compact card view for a project inside the Key Personnel detail modal.
 * Stripped-down version of MobileProjectRow, tailored for KeyPersonnelProject data shape.
 */
export const MobileKeyPersonnelProjectCard: React.FC<
	MobileKeyPersonnelProjectCardProps
> = ({ project, onClick, isLoading = false }) => {
	const isClickable = project.isCompanyProject && onClick;

	const locationDisplay = project.location
		? typeof project.location === "string"
			? project.location
			: (project.location as unknown as { value?: string })?.value
		: null;

	const sectorDisplay =
		project.sectors?.length > 0
			? project.sectors.map((s) => s.name).join(", ")
			: project.sector?.name;

	return (
		<button
			type="button"
			onClick={isClickable ? onClick : undefined}
			className={`w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition-shadow ${
				isClickable
					? "cursor-pointer hover:shadow-md"
					: "cursor-default"
			}`}
		>
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<h4 className="truncate text-sm font-semibold text-gray-900">
							{project.name}
						</h4>
						{project.isCompanyProject && (
							<span className="shrink-0 text-blue-700">
								{isLoading ? (
									<Spinner size="xs" />
								) : (
									<FiInfo size={14} />
								)}
							</span>
						)}
					</div>
					{project.role && (
						<p className="mt-0.5 text-xs text-gray-500">
							Role: {project.role}
						</p>
					)}
				</div>
			</div>

			{/* Sectors */}
			{sectorDisplay && (
				<div className="mt-2 flex flex-wrap gap-1">
					{project.sectors?.map((sector) => (
						<span
							key={sector.id}
							className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700"
						>
							{sector.name}
						</span>
					)) ?? (
						<span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
							{sectorDisplay}
						</span>
					)}
				</div>
			)}

			{/* Value & Location */}
			<div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
				{(project.value_humanized || project.value) && (
					<span>
						<span className="font-medium text-gray-700">Value:</span>{" "}
						{project.value_humanized || project.value}
					</span>
				)}
				{locationDisplay && (
					<span className="flex items-center gap-1">
						<FiMapPin size={10} />
						<span className="truncate">{locationDisplay}</span>
					</span>
				)}
			</div>
		</button>
	);
};
